import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, Clock, MapPin, ArrowLeft, Trophy, MessageCircle, Store, BadgeCheck, XCircle, Loader2,
} from "lucide-react";

function timeLeft(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3_600_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(ms / 60000))}m left`;
}

export default function InquiryDetail() {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inquiries/${inquiryId}`);
      setThread(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error("Inquiry not found");
      navigate("/inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [inquiryId]);

  const runBestDeal = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/inquiries/${inquiryId}/best-deal`);
      setThread((t) => ({ ...t, best_deal: data }));
      toast.success("Best-deal recommendation ready");
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI unavailable";
      toast.error(String(msg).replace(/_/g, " "));
    } finally {
      setAiBusy(false);
    }
  };

  const closeThread = async () => {
    if (!window.confirm("Close this inquiry? Vendors will no longer be able to reply.")) return;
    setClosing(true);
    try {
      await api.delete(`/inquiries/${inquiryId}`);
      toast.success("Inquiry closed");
      navigate("/inquiries");
    } catch (err) {
      toast.error("Could not close inquiry");
    } finally {
      setClosing(false);
    }
  };

  if (loading || !thread) {
    return (
      <div className="mx-auto max-w-screen-lg px-4 sm:px-6 lg:px-8 py-10">
        <Card className="p-10 text-center text-muted-foreground" data-testid="inq-detail-loading">Loading…</Card>
      </div>
    );
  }

  if (!thread.is_mine) {
    return (
      <div className="mx-auto max-w-screen-lg px-4 sm:px-6 lg:px-8 py-10">
        <Card className="p-10 border-border/60 text-center" data-testid="inq-detail-not-owner">
          <p className="text-sm text-muted-foreground">Only the original inquirer can open this thread in detail. Head back to the feed to reply.</p>
          <Button asChild className="rounded-full mt-4">
            <Link to="/inquiries"><ArrowLeft className="h-4 w-4 mr-1" /> Back to feed</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const replies = thread.replies || [];

  return (
    <div className="mx-auto max-w-screen-lg px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/inquiries" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="inq-detail-back">
        <ArrowLeft className="h-3 w-3" /> All inquiries
      </Link>

      <Card className="p-5 mt-3 border-purple-200/60 bg-purple-50/40 dark:bg-purple-950/10" data-testid="inq-detail-head">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-purple-700 dark:text-purple-300">Your inquiry</div>
            <p className="text-base sm:text-lg mt-1 whitespace-pre-wrap">{thread.text}</p>
            <div className="text-[11px] text-muted-foreground mt-3 flex items-center gap-3 flex-wrap">
              {thread.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {thread.city}</span>}
              {thread.category && <Badge variant="secondary" className="rounded-full text-[10px]">{thread.category}</Badge>}
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {timeLeft(thread.expires_at)}</span>
              <Badge className="bg-purple-600 text-white border-0">{replies.length} replies</Badge>
              {thread.is_closed && <Badge variant="destructive" className="rounded-full">Closed</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runBestDeal}
              disabled={aiBusy || replies.length === 0}
              className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white border-0"
              data-testid="inq-detail-best-deal-btn"
            >
              {aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trophy className="h-4 w-4 mr-1" />}
              Find best deal (AI)
            </Button>
            {!thread.is_closed && (
              <Button variant="outline" onClick={closeThread} disabled={closing} className="rounded-full" data-testid="inq-detail-close-btn">
                <XCircle className="h-4 w-4 mr-1" /> Close
              </Button>
            )}
          </div>
        </div>
      </Card>

      {thread.best_deal && (
        <Card className="p-5 mt-4 border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/10" data-testid="inq-detail-best-deal">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">AI Best-deal recommendation</div>
          </div>
          <pre className="text-sm whitespace-pre-wrap font-sans mt-2 text-foreground">{thread.best_deal.recommendation}</pre>
          <div className="text-[10px] text-muted-foreground mt-2">
            Considered {thread.best_deal.considered} replies · generated at {new Date(thread.best_deal.generated_at).toLocaleString()}
          </div>
        </Card>
      )}

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Vendor replies (visible only to you)</div>
        {replies.length === 0 ? (
          <Card className="p-10 border-border/60 text-center" data-testid="inq-detail-no-replies">
            <p className="text-sm text-muted-foreground">No replies yet. Vendors will start responding soon.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="inq-detail-replies">
            {replies.map((r) => (
              <Card key={r.reply_id} className="p-4 border-border/60" data-testid={`inq-detail-reply-${r.reply_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate inline-flex items-center gap-1">
                      <Store className="h-3.5 w-3.5 text-purple-600" />
                      {r.business_name || r.responder_name}
                      {r.vendor_id && <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {r.vendor_city || "—"} · {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  {r.price && (
                    <Badge className="bg-emerald-600 text-white border-0 shrink-0" data-testid={`inq-detail-reply-price-${r.reply_id}`}>
                      {r.price}
                    </Badge>
                  )}
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.text}</p>
                {r.vendor_id && (
                  <Button asChild size="sm" variant="outline" className="rounded-full mt-3" data-testid={`inq-detail-reply-chat-${r.reply_id}`}>
                    <Link to={`/vendors/${r.vendor_id}?chat=1`}>
                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> Chat with vendor
                    </Link>
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
